import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Collapse,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  LocalPharmacy as CounselingIcon,
  Restaurant as FoodIcon,
  Shield as PrecautionIcon,
  ReportProblem as SideEffectIcon,
  SwapHoriz as InteractionIcon,
  Medication as DrugInfoIcon,
  MedicalInformation as IndicationsIcon,
  Scale as DosageIcon,
  Science as IngredientsIcon,
  ContentCopy as DuplicateIcon,
  Block as ContraindicationIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useFdbClinicalData } from '../../hooks/queries/useFdbClinicalData';
import fdbService from '../../services/fdbService';
import DrugImageThumbnail from '../common/DrugImageThumbnail';
import type {
  FdbDrugInteraction,
  FdbSideEffect,
  FdbGeriatricPrecaution,
  FdbPediatricPrecaution,
  FdbPregnancyPrecaution,
  FdbLactationPrecaution,
} from '../../services/fdbService';
import { ageFromDob } from '../../utils/timeUtils';

interface FdbClinicalPanelProps {
  din: string | undefined;
  patientDob?: string;
  patientId?: string;
  pharmacyId?: string;
  patientSex?: string;
}

// ═══════════════════════════════════════════════════════════
//  Design System Constants
// ═══════════════════════════════════════════════════════════

// Side effect / precaution label maps
const FREQUENCY_LABELS: Record<string, string> = {
  '1': 'Common', '2': 'Infrequent', '3': 'Rare',
};
const SEVERITY_LABELS: Record<string, string> = {
  '1': 'Major', '2': 'Moderate', '3': 'Minor',
};
const SEVERITY_CHIP_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  '1': 'error', '2': 'warning', '3': 'info',
};

// DDI severity
const DDI_SEVERITY_COLORS: Record<number, 'error' | 'warning' | 'info' | 'default'> = {
  1: 'error', 2: 'error', 3: 'warning', 4: 'info',
};
const DDI_SEVERITY_LABELS: Record<number, string> = {
  1: 'Contraindicated', 2: 'Severe', 3: 'Moderate', 4: 'Minor',
};

// Card border colors (shared across DDI, Side Effects, Precautions)
const SEVERITY_BORDER: Record<number, string> = {
  1: '#d32f2f', 2: '#ed6c02', 3: '#0288d1', 4: '#9e9e9e',
};
const SEVERITY_BG: Record<number, string> = {
  1: 'rgba(211,47,47,0.04)', 2: 'rgba(237,108,2,0.03)', 3: 'transparent', 4: 'transparent',
};

// Side effect card borders (string keys '1','2','3')
const SE_SEVERITY_BORDER: Record<string, string> = {
  '1': '#d32f2f', '2': '#ed6c02', '3': '#0288d1',
};
const SE_SEVERITY_BG: Record<string, string> = {
  '1': 'rgba(211,47,47,0.04)', '2': 'rgba(237,108,2,0.03)', '3': 'transparent',
};

// Precaution category styles
const PREC_CATEGORY: Record<string, { label: string; border: string; bg: string }> = {
  geriatric: { label: 'Geriatric', border: '#7b1fa2', bg: 'rgba(123,31,162,0.04)' },
  pediatric: { label: 'Pediatric', border: '#00796b', bg: 'rgba(0,121,107,0.04)' },
  pregnancy: { label: 'Pregnancy', border: '#c62828', bg: 'rgba(198,40,40,0.04)' },
  lactation: { label: 'Lactation', border: '#ef6c00', bg: 'rgba(239,108,0,0.04)' },
};

// Progressive disclosure thresholds
const INITIAL_LINES = 4;
const SE_INITIAL_ITEMS = 4;
const PREC_INITIAL_ITEMS = 3;
const MAX_DRUGS_INITIAL = 5;

// ═══════════════════════════════════════════════════════════
//  DDI Helpers
// ═══════════════════════════════════════════════════════════

/** Replace "the latter drug" / "the former drug" with actual names */
function humanize(text: string, otherDrug: string, currentDrug: string): string {
  return text
    .replace(/\bthe latter drug\b/gi, otherDrug)
    .replace(/\bthe former drug\b/gi, currentDrug)
    .replace(/\bof drug\b/gi, `of ${otherDrug}`);
}

function groupBySeverity(interactions: FdbDrugInteraction[]): Map<number, FdbDrugInteraction[]> {
  const groups = new Map<number, FdbDrugInteraction[]>();
  for (const ddi of interactions) {
    const level = ddi.severity.level;
    if (!groups.has(level)) groups.set(level, []);
    groups.get(level)!.push(ddi);
  }
  return new Map([...groups.entries()].sort(([a], [b]) => a - b));
}

interface DrugDdiGroup {
  din: string;
  name: string;
  genericName: string;
  strength: string;
  bySeverity: Map<number, FdbDrugInteraction[]>;
  totalCount: number;
  highestSeverity: number;
}

function groupByDrug(interactions: FdbDrugInteraction[], currentDin: string): DrugDdiGroup[] {
  const drugMap = new Map<string, { name: string; genericName: string; strength: string; ddis: FdbDrugInteraction[] }>();
  for (const ddi of interactions) {
    const other = ddi.drug1.din === currentDin ? ddi.drug2 : ddi.drug1;
    if (!drugMap.has(other.din)) {
      drugMap.set(other.din, { name: other.name, genericName: other.genericName, strength: other.strength, ddis: [] });
    }
    drugMap.get(other.din)!.ddis.push(ddi);
  }
  return [...drugMap.entries()]
    .map(([din, { name, genericName, strength, ddis }]) => ({
      din, name, genericName, strength,
      bySeverity: groupBySeverity(ddis),
      totalCount: ddis.length,
      highestSeverity: Math.min(...ddis.map((d) => d.severity.level)),
    }))
    .sort((a, b) => a.highestSeverity - b.highestSeverity || a.name.localeCompare(b.name));
}

function formatFillDate(dateStr: string | undefined): string {
  if (!dateStr) return 'No fill on record';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ═══════════════════════════════════════════════════════════
//  DDI Sub-Components
// ═══════════════════════════════════════════════════════════

/** Lazy-loads full DDI monograph text from FDB */
const MonographDetail: React.FC<{ monographId: number }> = ({ monographId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fdb', 'ddi-monograph', monographId],
    queryFn: () => fdbService.getDdiMonograph(monographId),
    staleTime: 30 * 60 * 1000,
  });
  if (isLoading) return <CircularProgress size={14} sx={{ ml: 1, my: 0.5 }} />;
  if (error) return (
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic' }}>
      Monograph unavailable (ID: {monographId})
    </Typography>
  );
  if (!data?.text) return (
    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic' }}>
      No monograph text for this interaction.
    </Typography>
  );
  return (
    <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line', color: 'text.secondary', lineHeight: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
      {data.text}
    </Typography>
  );
};

/** Per-DDI expandable line — click "details" to see severity.description */
const InteractionLine: React.FC<{
  ddi: FdbDrugInteraction;
  effectText: string;
}> = ({ ddi, effectText }) => {
  const [showDetail, setShowDetail] = useState(false);

  // Format the FDB drug-class description as a readable subtitle
  const classDesc = ddi.description
    ? ddi.description.replace(/\//g, ' / ').replace(/\s+/g, ' ')
    : '';

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box
        onClick={() => setShowDetail(!showDetail)}
        sx={{
          display: 'flex', alignItems: 'flex-start', cursor: 'pointer',
          pl: 1.5, py: 0.25, position: 'relative', borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
          '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5 }}>
            {effectText}
          </Typography>
          {classDesc && (
            <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.6rem', lineHeight: 1.3, mt: -0.25 }}>
              {classDesc}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="primary.main" sx={{ ml: 0.5, whiteSpace: 'nowrap', fontSize: '0.6rem', mt: 0.25, userSelect: 'none' }}>
          {showDetail ? 'hide' : 'details'}
        </Typography>
      </Box>
      <Collapse in={showDetail} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 2.5, py: 0.5 }}>
          {ddi.severity.description && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', lineHeight: 1.4 }}>
              {ddi.severity.description}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/** Drug card — one Paper per interacting drug with severity-colored border */
const DrugCard: React.FC<{
  group: DrugDdiGroup;
  currentDin: string;
  currentDrugName: string;
  fillDate: string | undefined;
}> = ({ group, currentDin, currentDrugName, fillDate }) => {
  const [expanded, setExpanded] = useState(false);
  const [showMonograph, setShowMonograph] = useState(false);

  // Representative monographId for this drug pair (from first DDI)
  const firstDdi = [...group.bySeverity.values()][0]?.[0];
  const monographId = firstDdi?.monographId;

  const allSeverityEntries = [...group.bySeverity.entries()];
  const totalLines = allSeverityEntries.reduce((sum, [, ddis]) => sum + ddis.length, 0);
  const needsCollapse = totalLines > INITIAL_LINES;

  let linesRendered = 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: `4px solid ${SEVERITY_BORDER[group.highestSeverity] || '#9e9e9e'}`,
        bgcolor: SEVERITY_BG[group.highestSeverity] || 'transparent',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        {/* Drug header */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {group.name}
          </Typography>
          {group.genericName && group.genericName !== group.name && (
            <Typography variant="caption" color="text.secondary">
              {group.genericName}{group.strength ? ` ${group.strength}` : ''}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, fontSize: '0.65rem' }}>
          Last filled: {formatFillDate(fillDate)}
        </Typography>

        {/* Interaction lines by severity */}
        {allSeverityEntries.map(([level, ddis]) => {
          const lines = ddis.map((ddi, idx) => {
            linesRendered++;
            if (!expanded && linesRendered > INITIAL_LINES) return null;
            const otherDrug = ddi.drug1.din === currentDin ? ddi.drug2 : ddi.drug1;
            const effectText = ddi.clinicalEffect?.text
              ? humanize(ddi.clinicalEffect.text, otherDrug.name, currentDrugName)
              : ddi.description;
            return (
              <InteractionLine
                key={`${ddi.ddiCodex}-${ddi.drug1.din}-${ddi.drug2.din}-${idx}`}
                ddi={ddi}
                effectText={effectText}
              />
            );
          });

          return (
            <Box key={level} sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Chip
                  label={DDI_SEVERITY_LABELS[level]}
                  size="small"
                  color={DDI_SEVERITY_COLORS[level] || 'default'}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                />
                <Typography variant="caption" color="text.disabled">({ddis.length})</Typography>
              </Box>
              {lines}
            </Box>
          );
        })}

        {/* Show more / less */}
        {needsCollapse && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, mt: 0.5, color: 'primary.main' }}
          >
            {expanded ? 'Show less' : `Show ${totalLines - INITIAL_LINES} more`}
          </Button>
        )}

        {/* Monograph for this drug pair */}
        {monographId != null && monographId > 0 && (
          <>
            <Button
              size="small"
              onClick={() => setShowMonograph(!showMonograph)}
              sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, mt: 0.5, ml: needsCollapse ? 1.5 : 0, color: 'text.secondary' }}
            >
              {showMonograph ? 'Hide monograph' : 'View monograph'}
            </Button>
            <Collapse in={showMonograph} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 0.5 }}>
                <MonographDetail monographId={monographId} />
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════
//  Side Effects Sub-Components
// ═══════════════════════════════════════════════════════════

const SideEffectGroupCard: React.FC<{ severity: string; items: FdbSideEffect[] }> = ({ severity, items }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, SE_INITIAL_ITEMS);
  const hiddenCount = items.length - SE_INITIAL_ITEMS;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: `4px solid ${SE_SEVERITY_BORDER[severity] || '#9e9e9e'}`,
        bgcolor: SE_SEVERITY_BG[severity] || 'transparent',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Typography variant="body2" fontWeight={700}>
            {SEVERITY_LABELS[severity] || 'Other'} Side Effects
          </Typography>
          <Typography variant="caption" color="text.disabled">({items.length})</Typography>
        </Box>

        {visibleItems.map((se, idx) => (
          <Box
            key={idx}
            sx={{
              pl: 1.5, mb: 0.5, position: 'relative',
              '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5 }}>
                {se.description}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                {FREQUENCY_LABELS[se.frequency] || ''}
              </Typography>
              {se.requiresPhysicianNotification && (
                <Chip label="Notify MD" size="small" color="error" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700 }} />
              )}
              {se.requiresLabMonitoring && (
                <Chip label="Lab" size="small" color="warning" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700 }} />
              )}
              {se.isHypersensitivity && (
                <Chip label="Allergy" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
              )}
            </Box>
          </Box>
        ))}

        {hiddenCount > 0 && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, mt: 0.5, color: 'primary.main' }}
          >
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

const SideEffectsSection: React.FC<{ sideEffects: FdbSideEffect[] }> = ({ sideEffects }) => {
  const groups = new Map<string, FdbSideEffect[]>();
  for (const se of sideEffects) {
    const sev = se.severity || '3';
    if (!groups.has(sev)) groups.set(sev, []);
    groups.get(sev)!.push(se);
  }
  const sortedGroups = [...groups.entries()].sort(([a], [b]) => Number(a) - Number(b));

  const notifyMdCount = sideEffects.filter(se => se.requiresPhysicianNotification).length;
  const labCount = sideEffects.filter(se => se.requiresLabMonitoring).length;

  return (
    <>
      {/* Summary bar */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        {sortedGroups.map(([sev, items]) => (
          <Chip
            key={sev}
            label={`${items.length} ${SEVERITY_LABELS[sev] || 'Unknown'}`}
            size="small"
            color={SEVERITY_CHIP_COLOR[sev] || 'default'}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
          />
        ))}
        {notifyMdCount > 0 && (
          <Chip
            label={`${notifyMdCount} Notify MD`}
            size="small"
            color="error"
            sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
          />
        )}
        {labCount > 0 && (
          <Chip
            label={`${labCount} Lab Monitor`}
            size="small"
            color="warning"
            sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Severity group cards */}
      {sortedGroups.map(([sev, items]) => (
        <SideEffectGroupCard key={sev} severity={sev} items={items} />
      ))}
    </>
  );
};

// ═══════════════════════════════════════════════════════════
//  Precautions Sub-Components
// ═══════════════════════════════════════════════════════════

interface PrecautionsData {
  geriatric: FdbGeriatricPrecaution[];
  pediatric: FdbPediatricPrecaution[];
  pregnancy: FdbPregnancyPrecaution[];
  lactation: FdbLactationPrecaution[];
}

const PrecautionCategoryCard: React.FC<{
  category: string;
  items: any[];
  renderItem: (item: any, idx: number) => React.ReactNode;
}> = ({ category, items, renderItem }) => {
  const [expanded, setExpanded] = useState(false);
  const style = PREC_CATEGORY[category];
  const visibleItems = expanded ? items : items.slice(0, PREC_INITIAL_ITEMS);
  const hiddenCount = items.length - PREC_INITIAL_ITEMS;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: `4px solid ${style.border}`,
        bgcolor: style.bg,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Typography variant="body2" fontWeight={700}>
            {style.label}
          </Typography>
          <Typography variant="caption" color="text.disabled">({items.length})</Typography>
        </Box>

        {visibleItems.map((item, idx) => renderItem(item, idx))}

        {hiddenCount > 0 && (
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, mt: 0.5, color: 'primary.main' }}
          >
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

const PrecautionsSection: React.FC<{ precautions: PrecautionsData }> = ({ precautions }) => {
  const categories: { key: string; items: any[]; renderItem: (item: any, idx: number) => React.ReactNode }[] = [];

  if (precautions.geriatric.length > 0) {
    categories.push({
      key: 'geriatric',
      items: precautions.geriatric,
      renderItem: (g: FdbGeriatricPrecaution, idx: number) => (
        <Box key={idx} sx={{ pl: 1.5, mb: 0.75, position: 'relative', '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' } }}>
          <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
            {g.description}
          </Typography>
          {g.comment && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25, lineHeight: 1.4 }}>
              {g.comment}
            </Typography>
          )}
          {g.organSystems && Object.entries(g.organSystems).some(([, v]) => v) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25, pl: 1 }}>
              {Object.entries(g.organSystems).map(([organ, value]) =>
                value ? (
                  <Chip key={organ} label={`${organ}: ${value}`} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem', textTransform: 'capitalize' }} />
                ) : null,
              )}
            </Box>
          )}
        </Box>
      ),
    });
  }

  if (precautions.pediatric.length > 0) {
    categories.push({
      key: 'pediatric',
      items: precautions.pediatric,
      renderItem: (p: FdbPediatricPrecaution, idx: number) => (
        <Box key={idx} sx={{ pl: 1.5, mb: 0.75, position: 'relative', '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' } }}>
          <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
            {p.description}
          </Typography>
          {p.narrative && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25, lineHeight: 1.4 }}>
              {p.narrative}
            </Typography>
          )}
          {(p.minAgeDays != null || p.maxAgeDays != null) && (
            <Typography variant="caption" display="block" color="text.disabled" sx={{ pl: 1, fontSize: '0.6rem' }}>
              Age range: {p.minAgeDays != null ? `${Math.round(p.minAgeDays / 365)} yr` : 'birth'} – {p.maxAgeDays != null ? `${Math.round(p.maxAgeDays / 365)} yr` : 'adult'}
            </Typography>
          )}
        </Box>
      ),
    });
  }

  if (precautions.pregnancy.length > 0) {
    categories.push({
      key: 'pregnancy',
      items: precautions.pregnancy,
      renderItem: (p: FdbPregnancyPrecaution, idx: number) => (
        <Box key={idx} sx={{ pl: 1.5, mb: 0.75, position: 'relative', '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
              {p.description}
            </Typography>
            {p.severityLevelDescription && (
              <Chip label={p.severityLevelDescription} size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
            )}
          </Box>
          {p.narrative && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25, lineHeight: 1.4 }}>
              {p.narrative}
            </Typography>
          )}
        </Box>
      ),
    });
  }

  if (precautions.lactation.length > 0) {
    categories.push({
      key: 'lactation',
      items: precautions.lactation,
      renderItem: (l: FdbLactationPrecaution, idx: number) => (
        <Box key={idx} sx={{ pl: 1.5, mb: 0.75, position: 'relative', '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
              {l.description}
            </Typography>
            {l.severityLevelDescription && (
              <Chip label={l.severityLevelDescription} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
            )}
          </Box>
          {l.narrative && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1, mt: 0.25, lineHeight: 1.4 }}>
              {l.narrative}
            </Typography>
          )}
          {(l.excretionPotential?.description || l.effectsOnInfant?.description) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25, pl: 1 }}>
              {l.excretionPotential?.description && (
                <Chip label={`Excretion: ${l.excretionPotential.description}`} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
              )}
              {l.effectsOnInfant?.description && (
                <Chip label={`Infant: ${l.effectsOnInfant.description}`} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
              )}
            </Box>
          )}
        </Box>
      ),
    });
  }

  return (
    <>
      {/* Summary bar */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
        {categories.map(({ key, items }) => (
          <Chip
            key={key}
            label={`${items.length} ${PREC_CATEGORY[key].label}`}
            size="small"
            variant="outlined"
            sx={{
              height: 22, fontSize: '0.65rem', fontWeight: 600,
              borderColor: PREC_CATEGORY[key].border,
              color: PREC_CATEGORY[key].border,
            }}
          />
        ))}
      </Box>

      {/* Category cards */}
      {categories.map(({ key, items, renderItem }) => (
        <PrecautionCategoryCard key={key} category={key} items={items} renderItem={renderItem} />
      ))}
    </>
  );
};

// ═══════════════════════════════════════════════════════════
//  Compact collapsible helpers for About This Drug section
// ═══════════════════════════════════════════════════════════

const IndicationsCollapsible: React.FC<{ indicationsList: Array<{ diseaseDescription: string }> }> = ({ indicationsList }) => {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_COUNT = 4;
  const hasMore = indicationsList.length > INITIAL_COUNT;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="primary.main"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, cursor: hasMore ? 'pointer' : 'default' }}
        onClick={() => hasMore && setExpanded(!expanded)}
      >
        Indications ({indicationsList.length})
      </Typography>
      {indicationsList.slice(0, INITIAL_COUNT).map((ind, idx) => (
        <Typography key={idx} variant="caption" display="block" color="text.primary" sx={{ pl: 1, lineHeight: 1.4, fontSize: '0.65rem' }}>
          · {ind.diseaseDescription}
        </Typography>
      ))}
      <Collapse in={expanded} timeout="auto">
        {indicationsList.slice(INITIAL_COUNT).map((ind, idx) => (
          <Typography key={idx + INITIAL_COUNT} variant="caption" display="block" color="text.primary" sx={{ pl: 1, lineHeight: 1.4, fontSize: '0.65rem' }}>
            · {ind.diseaseDescription}
          </Typography>
        ))}
      </Collapse>
      {hasMore && !expanded && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ pl: 1, cursor: 'pointer', fontSize: '0.6rem' }}
          onClick={() => setExpanded(true)}
        >
          + {indicationsList.length - INITIAL_COUNT} more
        </Typography>
      )}
    </Box>
  );
};

const EduMonographsCollapsible: React.FC<{ eduMonographs: Array<{ monographId: number; sections: Array<{ section: string; text: string }> }> }> = ({ eduMonographs }) => {
  const [expanded, setExpanded] = useState(false);
  const allSections = eduMonographs.flatMap(m => m.sections.map((s, i) => ({ ...s, key: `${m.monographId}-${i}` })));
  const INITIAL_COUNT = 2;
  const hasMore = allSections.length > INITIAL_COUNT;
  return (
    <Box>
      {allSections.slice(0, INITIAL_COUNT).map((sec) => (
        <Box key={sec.key} sx={{ mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
            {sec.section}
          </Typography>
          <Typography variant="caption" display="block" color="text.primary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.4, fontSize: '0.65rem' }}>
            {sec.text.length > 200 && !expanded ? sec.text.substring(0, 200) + '...' : sec.text}
          </Typography>
        </Box>
      ))}
      <Collapse in={expanded} timeout="auto">
        {allSections.slice(INITIAL_COUNT).map((sec) => (
          <Box key={sec.key} sx={{ mb: 0.75 }}>
            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
              {sec.section}
            </Typography>
            <Typography variant="caption" display="block" color="text.primary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.4, fontSize: '0.65rem' }}>
              {sec.text}
            </Typography>
          </Box>
        ))}
      </Collapse>
      {hasMore && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: 'pointer', fontSize: '0.6rem' }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : `+ ${allSections.length - INITIAL_COUNT} more sections`}
        </Typography>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════
//  Main Panel
// ═══════════════════════════════════════════════════════════

const FdbClinicalPanel: React.FC<FdbClinicalPanelProps> = ({
  din,
  patientDob,
  patientId,
  pharmacyId,
  patientSex,
}) => {
  const patientAge = ageFromDob(patientDob);
  const {
    counseling, warnings, sideEffects, foodInteractions, precautions, patientEducation,
    drugInfo, indications, dosage, ingredients,
    durScreen, dinFillDates, isAnyLoading, dinNotInFdb,
  } = useFdbClinicalData(din, { patientAge, patientId, pharmacyId, patientGender: patientSex });

  const [showAllDrugs, setShowAllDrugs] = useState(false);

  if (!din) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" variant="outlined">No DIN available for FDB lookup.</Alert>
      </Box>
    );
  }

  if (isAnyLoading && !durScreen.data) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 4 }}>
        <CircularProgress size={28} />
        <Typography variant="caption" color="text.secondary">Loading FDB data...</Typography>
      </Box>
    );
  }

  const counselingMsgs = counseling.data?.counselingMessages ?? [];
  const warningList = warnings.data?.warnings ?? [];
  const sideEffectList = sideEffects.data?.sideEffects ?? [];
  const foodList = foodInteractions.data?.foodInteractions ?? [];
  const precData = precautions.data?.precautions;
  const precautionCount = precData
    ? precData.geriatric.length + precData.pediatric.length + precData.pregnancy.length + precData.lactation.length
    : 0;
  const eduMonographs = patientEducation.data?.monographs ?? [];
  const drugInfoData = drugInfo.data;
  const indicationsList = indications.data?.indications ?? [];
  const dosageRanges = dosage.data?.dosageRanges ?? [];
  const dailyDosing = dosage.data?.dailyDosing ?? [];
  const ingredientsList = ingredients.data?.ingredients ?? [];
  // Filter duplicate therapies & contraindications to current drug only
  const allDuplicateTherapies = durScreen.data?.duplicateTherapies ?? [];
  const duplicateTherapies = allDuplicateTherapies.filter(
    (dt) => dt.drugs.some((d) => d.din === din),
  );
  const allContraindications = durScreen.data?.drugDiseaseContraindications ?? [];
  const currentDrugContraindications = allContraindications.filter((ci) => ci.drug.din === din);
  const otherContraindications = allContraindications.filter((ci) => ci.drug.din !== din);

  // DDI data
  const allDdiList = durScreen.data?.drugDrugInteractions ?? [];
  const currentDrugDdis = allDdiList.filter((ddi) => ddi.drug1.din === din || ddi.drug2.din === din);
  const otherDdis = allDdiList.filter((ddi) => ddi.drug1.din !== din && ddi.drug2.din !== din);
  const currentDdiByDrug = groupByDrug(currentDrugDdis, din!);

  // Get current drug name for humanization
  const currentDrugName = durScreen.data?.drugDrugInteractions?.find(
    (ddi) => ddi.drug1.din === din,
  )?.drug1.name ?? durScreen.data?.drugDrugInteractions?.find(
    (ddi) => ddi.drug2.din === din,
  )?.drug2.name ?? 'this drug';

  // Severity summary counts for current drug DDIs
  const severityCounts: Record<number, number> = {};
  for (const ddi of currentDrugDdis) {
    severityCounts[ddi.severity.level] = (severityCounts[ddi.severity.level] || 0) + 1;
  }

  const drugsToShow = showAllDrugs ? currentDdiByDrug : currentDdiByDrug.slice(0, MAX_DRUGS_INITIAL);
  const hiddenDrugCount = currentDdiByDrug.length - MAX_DRUGS_INITIAL;

  if (dinNotInFdb && allDdiList.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" variant="outlined">
          No FDB clinical data available for DIN {din}. This drug may not be in the First DataBank database.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, overflow: 'auto', height: '100%' }}>
      <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, color: 'text.secondary', fontWeight: 600 }}>
        FDB Clinical Reference
      </Typography>
      <Typography variant="caption" sx={{ px: 1, display: 'block', mb: 1, color: 'text.disabled' }}>
        DIN: {din}
      </Typography>

      {/* ======== ABOUT THIS DRUG (Drug info + Patient Education monograph) ======== */}
      {(drugInfoData || eduMonographs.length > 0) && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DrugInfoIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>About This Drug</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, maxHeight: 400, overflow: 'auto' }}>
            {/* Drug info card — compact */}
            {drugInfoData && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                {din && <DrugImageThumbnail din={din} size={60} borderRadius={4} expandable />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>{drugInfoData.brandName || drugInfoData.labelName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                    {drugInfoData.genericName}{drugInfoData.strength ? ` ${drugInfoData.strength}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {drugInfoData.routeOfAdministration && (
                      <Chip label={drugInfoData.routeOfAdministration} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
                    )}
                    {drugInfoData.dosageFormCode && (
                      <Chip label={drugInfoData.dosageFormCode} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
                    )}
                    {drugInfoData.manufacturerDesc && (
                      <Chip label={drugInfoData.manufacturerDesc} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
                    )}
                  </Box>
                  {drugInfoData.ahfsDescription && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25, fontSize: '0.55rem' }}>
                      {drugInfoData.ahfsDescription}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Indications — compact, show first 4 with expand */}
            {indicationsList.length > 0 && (
              <IndicationsCollapsible indicationsList={indicationsList} />
            )}

            {/* Patient education monograph sections */}
            {eduMonographs.length > 0 && (
              <EduMonographsCollapsible eduMonographs={eduMonographs} />
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== PRECAUTIONS (moved to top) ======== */}
      {precautionCount > 0 && precData && (
        <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PrecautionIcon sx={{ fontSize: 18, mr: 1, color: 'info.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Precautions</Typography>
            <Chip label={precautionCount} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <PrecautionsSection precautions={precData} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== DDI SECTION ======== */}
      {currentDdiByDrug.length > 0 && (
        <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <InteractionIcon sx={{ fontSize: 18, mr: 1, color: 'error.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Drug Interactions</Typography>
            <Chip label={currentDrugDdis.length} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {/* Severity summary bar */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {[1, 2, 3, 4].map((level) =>
                severityCounts[level] ? (
                  <Chip
                    key={level}
                    label={`${severityCounts[level]} ${DDI_SEVERITY_LABELS[level]}`}
                    size="small"
                    color={DDI_SEVERITY_COLORS[level]}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
                  />
                ) : null,
              )}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', alignSelf: 'center' }}>
                {currentDdiByDrug.length} drug{currentDdiByDrug.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Drug cards */}
            {drugsToShow.map((group) => (
              <DrugCard
                key={group.din}
                group={group}
                currentDin={din!}
                currentDrugName={currentDrugName}
                fillDate={dinFillDates[group.din]}
              />
            ))}

            {/* Show more drugs */}
            {hiddenDrugCount > 0 && !showAllDrugs && (
              <Button
                fullWidth
                size="small"
                onClick={() => setShowAllDrugs(true)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', mt: 0.5, color: 'text.secondary' }}
              >
                + {hiddenDrugCount} more drug{hiddenDrugCount !== 1 ? 's' : ''} with interactions
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Other patient interactions (collapsed) */}
      {otherDdis.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <InteractionIcon sx={{ fontSize: 18, mr: 1, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ flex: 1 }}>Other Patient Interactions</Typography>
            <Chip label={otherDdis.length} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {otherDdis.length} interactions between the patient's other medications.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* DUR loading indicator */}
      {durScreen.isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading patient interactions...</Typography>
        </Box>
      )}

      {/* ======== NON-DDI SECTIONS ======== */}

      {/* Counseling Messages */}
      {counselingMsgs.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <CounselingIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Counseling Messages</Typography>
            <Chip label={counselingMsgs.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <List dense disablePadding>
              {counselingMsgs.map((msg) => (
                <ListItem key={msg.code} sx={{ px: 0.5, alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={msg.professionalMessage}
                    secondary={msg.patientMessage !== msg.professionalMessage ? `Patient: ${msg.patientMessage}` : undefined}
                    primaryTypographyProps={{ variant: 'caption', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { fontStyle: 'italic' } }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Warnings */}
      {warningList.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <WarningIcon sx={{ fontSize: 18, mr: 1, color: 'warning.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Warnings</Typography>
            <Chip label={warningList.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <List dense disablePadding>
              {warningList.map((w) => (
                <ListItem key={w.code} sx={{ px: 0.5 }}>
                  <ListItemText primary={w.text} primaryTypographyProps={{ variant: 'caption' }} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== SIDE EFFECTS (Apple-like cards) ======== */}
      {sideEffectList.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SideEffectIcon sx={{ fontSize: 18, mr: 1, color: 'error.light' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Side Effects</Typography>
            <Chip label={sideEffectList.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <SideEffectsSection sideEffects={sideEffectList} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Food Interactions */}
      {foodList.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <FoodIcon sx={{ fontSize: 18, mr: 1, color: 'success.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Food Interactions</Typography>
            <Chip label={foodList.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {foodList.map((fi) => (
              <Box key={fi.code} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={600}>{fi.message}</Typography>
                {fi.monograph && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25, whiteSpace: 'pre-line' }}>
                    {fi.monograph}
                  </Typography>
                )}
                {fi !== foodList[foodList.length - 1] && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}


      {/* ======== DUPLICATE THERAPIES (from DUR screen) ======== */}
      {duplicateTherapies.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DuplicateIcon sx={{ fontSize: 18, mr: 1, color: 'warning.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Duplicate Therapies</Typography>
            <Chip label={duplicateTherapies.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {duplicateTherapies.map((dt, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{ mb: 1, borderLeft: '4px solid #ed6c02', borderRadius: 1, overflow: 'hidden' }}
              >
                <Box sx={{ p: 1.5, pb: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{dt.therapyClass}</Typography>
                  {dt.therapyClassDetail && dt.therapyClassDetail !== dt.therapyClass && (
                    <Typography variant="caption" color="text.secondary" display="block">{dt.therapyClassDetail}</Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    {dt.drugs.map((drug) => (
                      <Box
                        key={drug.din}
                        sx={{
                          pl: 1.5, mb: 0.25, position: 'relative',
                          '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
                        }}
                      >
                        <Typography variant="caption" color="text.primary">{drug.name}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5, fontSize: '0.6rem' }}>
                          DIN: {drug.din}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== DRUG-DISEASE CONTRAINDICATIONS (current drug only) ======== */}
      {currentDrugContraindications.length > 0 && (
        <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ContraindicationIcon sx={{ fontSize: 18, mr: 1, color: 'error.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Drug-Disease Contraindications</Typography>
            <Chip label={currentDrugContraindications.length} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {currentDrugContraindications.map((ci, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderLeft: `4px solid ${SEVERITY_BORDER[ci.severityLevel] || '#d32f2f'}`,
                  bgcolor: SEVERITY_BG[ci.severityLevel] || 'transparent',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 1.5, pb: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="error.main">{ci.disease}</Typography>
                  {ci.reference && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25, fontSize: '0.6rem' }}>
                      {ci.reference}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Other patient contraindications (collapsed) */}
      {otherContraindications.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ContraindicationIcon sx={{ fontSize: 18, mr: 1, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ flex: 1 }}>Other Patient Contraindications</Typography>
            <Chip label={otherContraindications.length} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {otherContraindications.length} contraindications involving the patient's other medications.
            </Typography>
            {otherContraindications.slice(0, 5).map((ci, idx) => (
              <Box
                key={idx}
                sx={{
                  pl: 1.5, mb: 0.25, position: 'relative',
                  '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
                }}
              >
                <Typography variant="caption" color="text.primary">
                  {ci.drug.name} × <Typography component="span" variant="caption" color="error.main">{ci.disease}</Typography>
                </Typography>
              </Box>
            ))}
            {otherContraindications.length > 5 && (
              <Typography variant="caption" color="text.disabled" sx={{ pl: 1.5, fontSize: '0.6rem' }}>
                + {otherContraindications.length - 5} more
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== DOSAGE ======== */}
      {(dosageRanges.length > 0 || dailyDosing.length > 0) && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DosageIcon sx={{ fontSize: 18, mr: 1, color: 'secondary.main' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Dosage Information</Typography>
            <Chip label={dosageRanges.length + dailyDosing.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {dailyDosing.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                  Daily Dosing
                </Typography>
                {dailyDosing.map((dd, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ mb: 0.75, borderRadius: 1, p: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {dd.minDailyDose?.qty != null && (
                        <Chip
                          label={`Min: ${dd.minDailyDose.qty}${dd.minDailyDose.units ? ` ${dd.minDailyDose.units}` : ''}`}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                      {dd.maxDailyDose?.qty != null && (
                        <Chip
                          label={`Max: ${dd.maxDailyDose.qty}${dd.maxDailyDose.units ? ` ${dd.maxDailyDose.units}` : ''}`}
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                      {dd.doNotExceed?.qty != null && (
                        <Chip
                          label={`Do Not Exceed: ${dd.doNotExceed.qty}${dd.doNotExceed.units ? ` ${dd.doNotExceed.units}` : ''}`}
                          size="small"
                          color="error"
                          sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }}
                        />
                      )}
                    </Box>
                    {dd.ageRange && (dd.ageRange.minDays != null || dd.ageRange.maxDays != null) && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25, fontSize: '0.55rem' }}>
                        Age: {dd.ageRange.minDays != null ? `${Math.round(dd.ageRange.minDays / 365)}yr` : 'birth'} – {dd.ageRange.maxDays != null ? `${Math.round(dd.ageRange.maxDays / 365)}yr` : 'adult'}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
            {dosageRanges.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                  Dosage Ranges
                </Typography>
                {dosageRanges.slice(0, 6).map((dr, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      pl: 1.5, mb: 0.5, position: 'relative',
                      '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
                    }}
                  >
                    <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5 }}>
                      {dr.routeDescription || dr.routeOfAdmin}: {dr.lowDosePerDay ?? '?'}–{dr.highDosePerDay ?? '?'}/day
                      {dr.maxDosePerDay != null ? ` (max ${dr.maxDosePerDay})` : ''}
                    </Typography>
                    {dr.dosageTypeDescription && (
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.6rem' }}>
                        {dr.dosageTypeDescription}
                      </Typography>
                    )}
                  </Box>
                ))}
                {dosageRanges.length > 6 && (
                  <Typography variant="caption" color="text.disabled" sx={{ pl: 1.5, fontSize: '0.6rem' }}>
                    + {dosageRanges.length - 6} more ranges
                  </Typography>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ======== INGREDIENTS ======== */}
      {ingredientsList.length > 0 && (
        <Accordion disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <IngredientsIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>Drug Ingredients</Typography>
            <Chip label={ingredientsList.length} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1 }}>
            {ingredientsList.map((ing, idx) => (
              <Box
                key={idx}
                sx={{
                  pl: 1.5, mb: 0.5, position: 'relative',
                  '&::before': { content: '"·"', position: 'absolute', left: 4, fontWeight: 700, color: 'text.secondary' },
                }}
              >
                <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
                  {ing.ingredient || ing.description}
                </Typography>
                {ing.baseIngredient && ing.baseIngredient !== ing.ingredient && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5, fontSize: '0.6rem' }}>
                    (base: {ing.baseIngredient})
                  </Typography>
                )}
                {(ing.therapeuticClassification || ing.pharmacologicalClassification) && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    {ing.therapeuticClassification && (
                      <Chip label={ing.therapeuticClassification} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.5rem' }} />
                    )}
                    {ing.pharmacologicalClassification && (
                      <Chip label={ing.pharmacologicalClassification} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.5rem' }} />
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

    </Box>
  );
};

const accordionSx = {
  '&:before': { display: 'none' },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '4px !important',
  mb: 0.5,
  '& .MuiAccordionSummary-root': { minHeight: 36, py: 0 },
  '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center' },
};

export default FdbClinicalPanel;
